import FieldSetCollapseBar from '../FieldSetCollapseBar/FieldSetCollapseBar'
import Header from '../Header/Header'
import InfoPopOut from '../InfoPopOut/InfoPopOut'
import classes from './GridFieldset.module.scss'
import type { Placement } from '@eggplant/types'
import type { ReactNode } from 'react'

type collapseDirection = 'vertical' | 'horizontal'

export interface BaseProps {
  children?: ReactNode
  hasBackground?: boolean
  hasOverflow?: boolean
  hasCollapsible?: boolean
  query?: ReactNode
  collapseDirection?: collapseDirection
  defaultCollapse?: boolean
  isOverflowVisible?: boolean
  withPadding?: Placement | Placement[]
}

export interface LabelWithoutDescription {
  label?: string
  sectionLabel: never
  hasDivider: never
  description: never
  isRequired?: boolean
}

export interface LabelWithDescription {
  label: string
  sectionLabel?: never
  hasDivider?: never
  description?: ReactNode
  isRequired?: boolean
}

export interface SectionWithoutDescription {
  label: never
  sectionLabel?: string
  hasDivider?: boolean
  description: never
  isRequired?: never
}

export interface SectionWithDescription {
  label?: never
  sectionLabel: string
  hasDivider?: boolean
  description?: ReactNode
  isRequired?: never
}

export interface WithoutEither {
  label?: never
  sectionLabel?: never
  hasDivider?: never
  description?: never
  isRequired?: never
}

type Props = BaseProps & (LabelWithoutDescription | LabelWithDescription | SectionWithoutDescription | SectionWithDescription | WithoutEither)

// overflow only works for single FieldLabel
// eslint-disable-next-line complexity
const GridFieldset = ({
  children,
  label,
  sectionLabel,
  hasDivider = true,
  isRequired,
  description,
  hasOverflow,
  hasBackground,
  hasCollapsible = false,
  query,
  collapseDirection = 'horizontal',
  defaultCollapse = false,
  isOverflowVisible = false,
  withPadding
}: Props) => {
  const paddingClasses: Record<Placement, string> = {
    left: classes.PaddingLeft,
    right: classes.PaddingRight,
    top: classes.PaddingTop,
    bottom: classes.PaddingBottom
  }

  const resolvedPadding = (() => {
    if (withPadding === undefined) return []

    if (Array.isArray(withPadding)) return withPadding

    return [withPadding]
  })()

  const paddingClassNames = resolvedPadding.map(padding => paddingClasses[padding]).join(' ')

  const withBorderTop = sectionLabel && hasDivider
    ? classes.hasBorderTop
    : ''

  const queryAndDescriptionClasses = description && query
    ? classes.DescriptionAndQuery
    : classes.QueryOnly

  const legendClass = description || query
    ? queryAndDescriptionClasses
    : classes.WithAndWithoutDescription

  const legendWrapperClass = [
    legendClass,
    paddingClassNames
  ].filter(styleClass => styleClass).join(' ')

  const fieldsetWrapperClass = [
    classes.GridFieldset,
    withBorderTop
  ].filter(styleClass => styleClass).join(' ')

  const isWithoutDividerClass = sectionLabel && !hasDivider
    ? classes.extraBorderTop
    : ''

  const isOverflow = hasOverflow
    ? classes.isOverflow
    : classes.isOverflowHidden

  const backgroundClass = hasBackground
    ? classes.hasBackground
    : ''

  const overflowClass = isOverflowVisible
    ? ''
    : isOverflow

  const childrenWrapperClass = [
    classes.GridChildren,
    isWithoutDividerClass,
    overflowClass,
    backgroundClass
  ].filter(value => value).join(' ')

  const renderChildren =
    <fieldset className={fieldsetWrapperClass}>
      {
        (label || sectionLabel) &&
          <legend className={legendWrapperClass}>
            {
              !!sectionLabel &&
                <Header
                  hasNoTopMargin
                  rank={3}
                >
                  {sectionLabel}
                </Header>
            }
            {
              !!label && label
            }
            <span className={classes.RequiredIcon}>
              {
                isRequired
                  ? '*'
                  : ''
              }
            </span>
            {
              description &&
                <div className={sectionLabel && classes.DescriptionAlignment}>
                  <InfoPopOut>
                    {description}
                  </InfoPopOut>
                </div>
            }
            {
              query &&
                query
            }
          </legend>
      }
      <div className={childrenWrapperClass}>
        {children}
      </div>
    </fieldset>

  return hasCollapsible
    ? (
      <FieldSetCollapseBar
        collapseDirection={collapseDirection}
        defaultCollapse={defaultCollapse}
      >
        {renderChildren}
      </FieldSetCollapseBar>
    )
    : renderChildren
}

export default GridFieldset
